import { X } from "lucide-react";
import { useState } from "react";

import { MemberSearchCombobox } from "@/components/member-search-combobox";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type Group,
  isApiError,
  useAddGroupMember,
  useGroupMembers,
  useRemoveGroupMember,
  type UserSearchResult,
} from "@/lib/api";
import { logger } from "@/lib/logger";

interface GroupMembersDialogProps {
  group: Group | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GroupMembersDialog({ group, open, onOpenChange }: GroupMembersDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{group ? `${group.name} Members` : "Group Members"}</DialogTitle>
          <DialogDescription>
            Members can see and be assigned this group&apos;s projects.
          </DialogDescription>
        </DialogHeader>
        {group && open && (
          // Remount per group so query state (and the search box) resets
          // cleanly when switching between groups.
          <GroupMembersContent key={group.id} groupId={group.id} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function GroupMembersContent({ groupId }: { groupId: number }) {
  const { data: members = [], isLoading } = useGroupMembers(groupId);
  const addMember = useAddGroupMember(groupId);
  const removeMember = useRemoveGroupMember(groupId);
  const [error, setError] = useState("");

  const handleAddMember = async (user: UserSearchResult) => {
    setError("");
    try {
      await addMember.mutateAsync({ userId: user.id });
    } catch (err) {
      logger.error("[group-members] add failed:", err);
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      setError(detail ?? "Failed to add member.");
    }
  };

  const handleRemoveMember = async (userId: string) => {
    setError("");
    try {
      await removeMember.mutateAsync({ userId });
    } catch (err) {
      logger.error("[group-members] remove failed:", err);
      setError("Failed to remove member.");
    }
  };

  return (
    <DialogBody>
      <div className="space-y-4">
        {isLoading ? (
          <p className="text-muted-foreground py-8 text-center text-sm">Loading...</p>
        ) : members.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">No members yet.</p>
        ) : (
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {members.map((member) => (
              <div
                key={member.user_id}
                className="flex items-center justify-between rounded-md px-2 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-foreground truncate text-sm font-medium">
                    {member.full_name || member.email.split("@")[0]}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">{member.email}</p>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  onClick={() => handleRemoveMember(member.user_id)}
                  disabled={removeMember.isPending}
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <MemberSearchCombobox
          excludeIds={members.map((m) => m.user_id)}
          onSelect={handleAddMember}
          disabled={addMember.isPending}
        />
        {error && <p className="text-destructive text-xs">{error}</p>}
      </div>
    </DialogBody>
  );
}
